import React from 'react';
import { DollarSign, Users, Server as ServerIcon, Zap, Snowflake, Clock, Landmark, Minus, Plus, AlertTriangle, Wrench, Gauge, UserCheck, CalendarDays, Globe, TrendingDown, TrendingUp, Star } from 'lucide-react';
import { STAFF_ROLE_CONFIGS } from '../constants';

interface Stats {
  totalRevenue: number;
  totalCustomers: number;
  totalServers: number;
}

interface StatsBarProps {
  stats: Stats;
  money: number;
  formatCurrency: (amount: number, decimals?: number) => string;
  operationalCosts?: { power: number; cooling: number; maintenance: number; staff?: number };
  currentHour?: number;
  currentMinute?: number;
  onScrubHour?: (newHour: number) => void;
  currentPowerRateKWh?: number;
  currentTariffPhase?: 'Peak' | 'Off-peak' | 'Mid' | 'Fixed';
  showPowerWarning?: boolean;
  powerWarningMessage?: string;
  pue?: number;
  gameDay?: number;
  gameMonthName?: string;
  gameMonth?: number;
  revenueHistory?: number[];
  transitCostPerDay?: number;
  bandwidthPenaltyPerDay?: number;
  bandwidthBreakdown?: { torPenalty: number; hdaPenalty: number; corePenalty: number; saturatedTorRacks: number; saturatedHdaFloors: number; saturatedCoreLocations: number };
  ixpRevenueBonusPerDay?: number;
  coolingStats?: { used: number; capacity: number };
  transitStats?: { provisionedGbps: number; demandGbps: number; totalTrafficGbps: number; hasAnyMmr: boolean };
  reputation?: number;
  eventRevenueModifier?: number;
  netPerDay?: number; // authoritative daily net from App (matches game-loop accrual)
  activeEvents?: Array<{ id: string; name: string; locationId: string; remainingDuration: number; duration: number; positive?: boolean }>;
  revenueBreakdown?: {
    serversWithRevenue: number;
    serversDown: number;
    locationsNoMmr: number;
    locationsNoTransit: number;
    locationsPartial: number;
    reputationPct: number;
  };
}

// Format Gbps with appropriate precision: Mbps for < 1 Gbps, 1 decimal for < 10, whole number above
function formatGbps(gbps: number): string {
  if (gbps === 0) return '0 Mbps';
  if (gbps < 0.001) { const kbps = Math.round(gbps * 1000000); return kbps >= 1000 ? `${(gbps * 1000).toFixed(1)} Mbps` : `${kbps} Kbps`; }
  if (gbps < 1) return `${Math.round(gbps * 1000)} Mbps`;
  if (gbps < 10) return `${gbps.toFixed(1)} Gbps`;
  return `${Math.round(gbps)} Gbps`;
}

// Tiny inline SVG sparkline — width×height in px, no deps
function Sparkline({ values, width = 48, height = 20 }: { values: number[]; width?: number; height?: number }) {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  const color = last >= prev ? '#34d399' : '#f87171'; // emerald or red
  return (
    <svg width={width} height={height} className="inline-block align-middle">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const StatsBar: React.FC<StatsBarProps> = ({
  stats,
  money,
  formatCurrency,
  operationalCosts = { power: 0, cooling: 0, maintenance: 0 },
  currentHour = 0,
  currentMinute = 0,
  onScrubHour,
  currentPowerRateKWh,
  currentTariffPhase,
  showPowerWarning,
  powerWarningMessage,
  pue,
  gameDay,
  gameMonthName,
  gameMonth,
  revenueHistory,
  transitCostPerDay = 0,
  bandwidthPenaltyPerDay = 0,
  bandwidthBreakdown,
  ixpRevenueBonusPerDay = 0,
  revenueBreakdown,
  coolingStats,
  transitStats,
  reputation,
  eventRevenueModifier = 0,
  netPerDay,
  activeEvents = [],
}) => {
  const hh = String(currentHour).padStart(2, '0');
  const mm = String(currentMinute).padStart(2, '0');
  const phaseClass =
    currentTariffPhase === 'Peak' ? 'bg-red-600/80 text-white' :
    currentTariffPhase === 'Off-peak' ? 'bg-emerald-600/80 text-white' :
    currentTariffPhase === 'Mid' ? 'bg-amber-600/80 text-white' :
    'bg-slate-600/80 text-white';

  const pueLabel = pue == null ? null :
    pue < 1.4 ? { text: 'Excellent', cls: 'text-green-400' } :
    pue < 2.0 ? { text: 'Good', cls: 'text-yellow-400' } :
    { text: 'Poor', cls: 'text-red-400' };

  return (
    <div className="bg-gray-800 rounded-lg px-2 py-1 mb-4">
      <div className="flex items-center flex-wrap gap-y-1 divide-x divide-gray-700">

        {/* Revenue */}
        {(() => {
          const rb = revenueBreakdown;
          const hasIssue = rb && (rb.locationsNoMmr > 0 || rb.locationsNoTransit > 0 || rb.serversDown > 0 || rb.locationsPartial > 0);
          const lines: string[] = [];
          if (rb) {
            lines.push(`${rb.serversWithRevenue} servers earning revenue`);
            if (rb.serversDown > 0) lines.push(`⚠ ${rb.serversDown} server(s) down/repairing — earning $0`);
            if (rb.locationsNoMmr > 0) lines.push(`⚠ ${rb.locationsNoMmr} location(s) have no MMR — servers earn $0`);
            if (rb.locationsNoTransit > 0) lines.push(`⚠ ${rb.locationsNoTransit} location(s) have no transit/IXP — servers earn $0`);
            if (rb.locationsPartial > 0) lines.push(`⚠ ${rb.locationsPartial} location(s) transit capacity < server demand (partial revenue)`);
          }
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0 relative group cursor-help">
              <DollarSign className={`w-4 h-4 ${hasIssue ? 'text-amber-400' : 'text-green-400'}`} />
              <div className="flex flex-col">
                <div className="text-xs text-gray-400">Revenue</div>
                <div className="font-bold flex items-center gap-1.5">
                  {formatCurrency(stats.totalRevenue)}/day
                  {hasIssue && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                  {revenueHistory && revenueHistory.length >= 2 && (
                    <Sparkline values={revenueHistory} width={48} height={18} />
                  )}
                </div>
              </div>
              {rb && (
                <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[260px] text-xs">
                  <div className="font-semibold text-gray-200 mb-2">Revenue breakdown</div>
                  {lines.map((l, i) => (
                    <div key={i} className={`py-0.5 ${l.startsWith('⚠') ? 'text-amber-400' : 'text-gray-300'}`}>{l}</div>
                  ))}
                  {lines.length === 1 && <div className="text-emerald-400 mt-1">All systems nominal ✓</div>}
                </div>
              )}
            </div>
          );
        })()}

        {/* Money */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0">
          <Landmark className="w-4 h-4 text-emerald-400" />
          <div className="flex flex-col">
            <div className="text-xs text-gray-400">Money</div>
            <div className="font-bold text-green-400">{formatCurrency(money)}</div>
          </div>
        </div>

        {/* In-game clock */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0" title="In-game time">
          <Clock className="w-4 h-4 text-gray-300" />
          <span className="font-mono text-sm">{hh}:{mm}</span>
        </div>

        {/* In-game calendar */}
        {gameDay != null && gameMonthName && (
          <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0" title={`In-game date — Month ${gameMonth}`}>
            <CalendarDays className="w-4 h-4 text-indigo-400" />
            <div className="flex flex-col">
              <div className="text-xs text-gray-400">Date</div>
              <div className="font-bold text-indigo-300">{gameMonthName} {gameDay}</div>
            </div>
          </div>
        )}

        {/* Time scrubber */}
        <div className="flex items-center gap-1 px-2 py-1 whitespace-nowrap shrink-0" title="Set in-game hour">
          <button
            className="p-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            onClick={() => onScrubHour && onScrubHour((currentHour + 23) % 24)}
            aria-label="Decrease hour"
          >
            <Minus className="w-3.5 h-3.5 text-gray-200" />
          </button>
          <input
            type="range"
            min={0}
            max={23}
            value={currentHour}
            onChange={(e) => onScrubHour && onScrubHour(parseInt(e.target.value, 10))}
            className="w-24 h-2 accent-emerald-500 cursor-pointer"
          />
          <button
            className="p-1 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
            onClick={() => onScrubHour && onScrubHour((currentHour + 1) % 24)}
            aria-label="Increase hour"
          >
            <Plus className="w-3.5 h-3.5 text-gray-200" />
          </button>
        </div>

        {/* Customers */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0">
          <Users className="w-4 h-4 text-blue-400" />
          <div>
            <div className="text-xs text-gray-400">Customers</div>
            <div className="font-bold">{stats.totalCustomers.toLocaleString()}</div>
          </div>
        </div>

        {/* Servers */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0">
          <ServerIcon className="w-4 h-4 text-purple-400" />
          <div>
            <div className="text-xs text-gray-400">Servers</div>
            <div className="font-bold">{stats.totalServers}</div>
          </div>
        </div>

        {/* Power Costs */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0">
          <Zap className="w-4 h-4 text-red-400" />
          <div>
            <div className="text-xs text-gray-400">Power Costs</div>
            <div className="font-bold text-red-400">-{formatCurrency(operationalCosts.power)}/day</div>
            {typeof currentPowerRateKWh === 'number' && (
              <div className="flex items-center gap-1 mt-0.5">
                {currentTariffPhase && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${phaseClass}`}>{currentTariffPhase}</span>
                )}
                <span className="text-[11px] text-gray-300">${formatCurrency(currentPowerRateKWh, 3)}/kWh</span>
              </div>
            )}
            {showPowerWarning && (
              <div className="flex items-center gap-1 mt-0.5" title={powerWarningMessage}>
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-600/80 text-white inline-flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Power setup incomplete
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Cooling Costs + Capacity Bar */}
        {(() => {
          const used = coolingStats?.used ?? 0;
          const cap = coolingStats?.capacity ?? 0;
          const pct = cap > 0 ? Math.min(100, (used / cap) * 100) : 0;
          const barColor = pct > 90 ? 'bg-red-500' : pct > 70 ? 'bg-amber-400' : 'bg-blue-400';
          const label = pct > 90 ? 'text-red-400' : pct > 70 ? 'text-amber-400' : 'text-blue-400';
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0"
              title={cap > 0 ? `Cooling: ${used.toFixed(1)} / ${cap.toFixed(1)} kW used${pct > 90 ? ' — CRITICAL: add more chillers!' : pct > 70 ? ' — install more chillers soon' : ''}` : 'No chillers installed yet'}>
              <Snowflake className={`w-4 h-4 ${label}`} />
              <div>
                <div className="text-xs text-gray-400">Cooling</div>
                <div className="font-bold text-red-400">-{formatCurrency(operationalCosts.cooling)}/day</div>
                {cap > 0 && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className={`text-[10px] font-mono ${label}`}>{Math.round(pct)}%</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Maintenance Costs */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0">
          <Wrench className="w-4 h-4 text-orange-400" />
          <div>
            <div className="text-xs text-gray-400">Maintenance</div>
            <div className="font-bold text-orange-400">-{formatCurrency(operationalCosts.maintenance)}/day</div>
          </div>
        </div>

        {/* Staff Costs — always visible so players can plan before hiring */}
        {(() => {
          const staffCost = operationalCosts.staff ?? 0;
          const hasStaff = staffCost > 0;
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0 relative group cursor-help">
              <UserCheck className={`w-4 h-4 ${hasStaff ? 'text-rose-400' : 'text-gray-600'}`} />
              <div>
                <div className={`text-xs ${hasStaff ? 'text-gray-400' : 'text-gray-600'}`}>Staff</div>
                <div className={`font-bold ${hasStaff ? 'text-rose-400' : 'text-gray-600'}`}>
                  {hasStaff ? `-${formatCurrency(staffCost)}/day` : 'None hired'}
                </div>
              </div>
              <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[260px] text-xs">
                {hasStaff ? (
                  <>
                    <div className="font-semibold text-gray-200 mb-2">Active staff — {formatCurrency(staffCost)}/day</div>
                    <div className="space-y-1 mb-2">
                      {STAFF_ROLE_CONFIGS.map(cfg => (
                        <div key={cfg.role} className="flex justify-between">
                          <span className="text-gray-300">{cfg.name}</span>
                          <span className="text-rose-400">${Math.round(cfg.monthlySalary / 30)}/day each</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-2 border-t border-gray-700 text-gray-500">Manage via Management → Staff</div>
                  </>
                ) : (
                  <>
                    <div className="font-semibold text-gray-200 mb-2">No staff hired yet</div>
                    <div className="text-gray-400 mb-2">Hire staff via Management → Staff. Roles:</div>
                    <div className="space-y-1">
                      {STAFF_ROLE_CONFIGS.map(cfg => (
                        <div key={cfg.role} className="flex justify-between">
                          <span className="text-gray-300">{cfg.name}</span>
                          <span className="text-rose-400">${Math.round(cfg.monthlySalary / 30)}/day</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-700 text-gray-500">Technicians repair servers automatically. Security Officers reduce DDoS risk.</div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* IXP Revenue Bonus — always shown */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0" title={ixpRevenueBonusPerDay > 0 ? 'Extra revenue from IXP peering agreements' : 'Connect to an IXP in the MMR to earn a peering revenue bonus'}>
          <Globe className={`w-4 h-4 ${ixpRevenueBonusPerDay > 0 ? 'text-cyan-400' : 'text-gray-600'}`} />
          <div>
            <div className={`text-xs ${ixpRevenueBonusPerDay > 0 ? 'text-gray-400' : 'text-gray-600'}`}>IXP Bonus</div>
            <div className={`font-bold ${ixpRevenueBonusPerDay > 0 ? 'text-cyan-400' : 'text-gray-600'}`}>
              {ixpRevenueBonusPerDay > 0 ? `+${formatCurrency(ixpRevenueBonusPerDay)}/day` : '—'}
            </div>
          </div>
        </div>

        {/* Live Traffic — always shown */}
        {(() => {
          const { provisionedGbps, demandGbps, totalTrafficGbps, hasAnyMmr } = transitStats ?? { provisionedGbps: 0, demandGbps: 0, totalTrafficGbps: 0, hasAnyMmr: false };
          const hasTraffic = totalTrafficGbps > 0;
          const pct = demandGbps > 0 && provisionedGbps > 0 ? Math.min(100, (demandGbps / provisionedGbps) * 100) : 0;
          const isSaturated = hasAnyMmr && demandGbps > provisionedGbps && provisionedGbps > 0;
          const isTight = !isSaturated && pct >= 75;
          const trafficColor = !hasTraffic ? 'text-gray-600' : isSaturated ? 'text-red-400' : isTight ? 'text-amber-400' : 'text-emerald-400';
          const barFill = isSaturated ? 'bg-red-500' : isTight ? 'bg-amber-400' : 'bg-emerald-500';
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0 relative group cursor-help">
              <Globe className={`w-4 h-4 ${trafficColor}`} />
              <div>
                <div className={`text-xs ${hasTraffic ? 'text-gray-400' : 'text-gray-600'}`}>Traffic</div>
                <div className={`font-bold ${trafficColor}`}>
                  {hasTraffic
                    ? (hasAnyMmr && provisionedGbps > 0
                        ? <>{formatGbps(demandGbps)} <span className="text-gray-500 font-normal">/</span> {formatGbps(provisionedGbps)}</>
                        : formatGbps(totalTrafficGbps))
                    : '—'}
                </div>
                {hasAnyMmr && provisionedGbps > 0 && hasTraffic && (
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all duration-300 ${barFill}`} style={{ width: `${Math.min(100, pct)}%` }} />
                    </div>
                    <span className={`text-[10px] font-mono ${trafficColor}`}>{Math.round(pct)}%</span>
                  </div>
                )}
              </div>
              <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[230px] text-xs">
                <div className="font-semibold text-gray-200 mb-2">Live traffic</div>
                {hasTraffic ? (
                  <>
                    <div className="flex justify-between py-0.5">
                      <span className="text-gray-400">Total server output</span>
                      <span className="text-gray-200">{formatGbps(totalTrafficGbps)}</span>
                    </div>
                    {hasAnyMmr && (
                      <>
                        <div className="flex justify-between py-0.5">
                          <span className="text-gray-400">Routed via MMR</span>
                          <span className="text-gray-200">{formatGbps(demandGbps)}</span>
                        </div>
                        <div className="flex justify-between py-0.5">
                          <span className="text-gray-400">Transit capacity</span>
                          <span className={provisionedGbps > 0 ? 'text-gray-200' : 'text-red-400'}>{provisionedGbps > 0 ? formatGbps(provisionedGbps) : 'None ordered'}</span>
                        </div>
                        {isSaturated && <div className="mt-1.5 pt-1.5 border-t border-gray-700 text-red-400">⚠ Transit saturated — order more in the MMR</div>}
                      </>
                    )}
                    {!hasAnyMmr && <div className="text-amber-400 mt-1">Build MMR + order transit to route this traffic externally</div>}
                  </>
                ) : (
                  <div className="text-gray-500">No servers installed yet</div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Transit Costs — always shown */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0" title={transitCostPerDay > 0 ? 'Daily transit provider and IXP port fees' : 'Order transit in the MMR to connect to the internet'}>
          <Globe className={`w-4 h-4 ${transitCostPerDay > 0 ? 'text-red-400' : 'text-gray-600'}`} />
          <div>
            <div className={`text-xs ${transitCostPerDay > 0 ? 'text-gray-400' : 'text-gray-600'}`}>Transit</div>
            <div className={`font-bold ${transitCostPerDay > 0 ? 'text-red-400' : 'text-gray-600'}`}>
              {transitCostPerDay > 0 ? `-${formatCurrency(transitCostPerDay)}/day` : '—'}
            </div>
          </div>
        </div>

        {/* Bandwidth Penalty — always shown, tooltip only when active */}
        <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0 relative group cursor-help">
          <TrendingDown className={`w-4 h-4 ${bandwidthPenaltyPerDay > 0 ? 'text-orange-400' : 'text-gray-600'}`} />
          <div>
            <div className={`text-xs ${bandwidthPenaltyPerDay > 0 ? 'text-gray-400' : 'text-gray-600'}`}>BW Penalty</div>
            <div className={`font-bold ${bandwidthPenaltyPerDay > 0 ? 'text-orange-400' : 'text-gray-600'}`}>
              {bandwidthPenaltyPerDay > 0 ? `-${formatCurrency(bandwidthPenaltyPerDay)}/day` : '—'}
            </div>
          </div>
          <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[240px] text-xs">
            <div className="font-semibold text-gray-200 mb-2">Bandwidth bottlenecks</div>
            {bandwidthPenaltyPerDay > 0 && bandwidthBreakdown ? (
              <>
                {bandwidthBreakdown.torPenalty > 0 && (
                  <div className="flex justify-between py-0.5">
                    <span className="text-amber-400">TOR Switch ({bandwidthBreakdown.saturatedTorRacks} rack{bandwidthBreakdown.saturatedTorRacks !== 1 ? 's' : ''})</span>
                    <span className="text-orange-400 ml-4">-{formatCurrency(bandwidthBreakdown.torPenalty)}/day</span>
                  </div>
                )}
                {bandwidthBreakdown.hdaPenalty > 0 && (
                  <div className="flex justify-between py-0.5">
                    <span className="text-amber-400">HDA Switch ({bandwidthBreakdown.saturatedHdaFloors} floor{bandwidthBreakdown.saturatedHdaFloors !== 1 ? 's' : ''})</span>
                    <span className="text-orange-400 ml-4">-{formatCurrency(bandwidthBreakdown.hdaPenalty)}/day</span>
                  </div>
                )}
                {bandwidthBreakdown.corePenalty > 0 && (
                  <div className="flex justify-between py-0.5">
                    <span className="text-amber-400">Core Switch ({bandwidthBreakdown.saturatedCoreLocations} location{bandwidthBreakdown.saturatedCoreLocations !== 1 ? 's' : ''})</span>
                    <span className="text-orange-400 ml-4">-{formatCurrency(bandwidthBreakdown.corePenalty)}/day</span>
                  </div>
                )}
                <div className="mt-1.5 pt-1.5 border-t border-gray-700 text-gray-500">Upgrade the bottleneck layer to reduce this penalty</div>
              </>
            ) : (
              <div className="text-gray-500">No bandwidth penalties — all links within capacity ✓</div>
            )}
          </div>
        </div>

        {/* Active Events — always shown */}
        {(() => {
          const positiveEvents = activeEvents.filter(e => e.positive);
          const negativeEvents = activeEvents.filter(e => !e.positive);
          const hasEvents = activeEvents.length > 0;
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0 relative group cursor-help">
              <AlertTriangle className={`w-4 h-4 ${!hasEvents ? 'text-gray-600' : negativeEvents.length > 0 ? 'text-amber-400' : 'text-emerald-400'}`} />
              <div>
                <div className={`text-xs ${hasEvents ? 'text-gray-400' : 'text-gray-600'}`}>Events</div>
                <div className="font-bold">
                  {!hasEvents ? (
                    <span className="text-gray-600">Quiet</span>
                  ) : (
                    <>
                      {positiveEvents.length > 0 && <span className="text-emerald-400">+{positiveEvents.length}</span>}
                      {positiveEvents.length > 0 && negativeEvents.length > 0 && <span className="text-gray-500"> / </span>}
                      {negativeEvents.length > 0 && <span className="text-amber-400">-{negativeEvents.length}</span>}
                    </>
                  )}
                </div>
              </div>
              <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[240px] text-xs">
                <div className="font-semibold text-gray-200 mb-2">Active events</div>
                {hasEvents ? (
                  <>
                    {activeEvents.map((e, i) => {
                      const pct = Math.round((1 - e.remainingDuration / e.duration) * 100);
                      return (
                        <div key={i} className={`py-0.5 ${e.positive ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {e.positive ? '▲' : '▼'} {e.name} ({pct}% elapsed)
                        </div>
                      );
                    })}
                    {eventRevenueModifier !== 0 && (
                      <div className={`mt-1.5 pt-1.5 border-t border-gray-700 font-semibold ${eventRevenueModifier > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        Net event impact: {eventRevenueModifier > 0 ? '+' : ''}{formatCurrency(eventRevenueModifier)}/day
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-gray-500">No active events — all systems nominal ✓</div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Net $/day */}
        {(() => {
          // Prefer the authoritative net from App (matches actual money accrual); fall back
          // to a local estimate only if not provided.
          const net = netPerDay ?? (
            stats.totalRevenue + ixpRevenueBonusPerDay + eventRevenueModifier
            - (operationalCosts.power + operationalCosts.cooling + operationalCosts.maintenance + (operationalCosts.staff ?? 0) + transitCostPerDay + bandwidthPenaltyPerDay)
          );
          const isPositive = net >= 0;
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0" title="Net daily income after all costs and active event modifiers">
              {isPositive ? <TrendingUp className="w-4 h-4 text-emerald-400" /> : <TrendingDown className="w-4 h-4 text-red-400" />}
              <div>
                <div className="text-xs text-gray-400">Net/day</div>
                <div className={`font-bold ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                  {isPositive ? '+' : ''}{formatCurrency(net)}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Reputation */}
        {reputation != null && (() => {
          const rep = Math.round(reputation);
          const color = rep >= 90 ? 'text-emerald-400' : rep >= 70 ? 'text-yellow-400' : rep >= 50 ? 'text-orange-400' : 'text-red-400';
          const label = rep >= 90 ? 'Excellent' : rep >= 70 ? 'Good' : rep >= 50 ? 'Fair' : 'Poor';
          const revenueImpact = rep < 100 ? `Revenue reduced by ${(100 - rep).toFixed(0)}%` : rep > 100 ? `Revenue boosted by ${(rep - 100).toFixed(0)}%` : 'Full revenue earned';
          return (
            <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0 relative group cursor-help">
              <Star className={`w-4 h-4 ${color}`} />
              <div>
                <div className="text-xs text-gray-400">Reputation</div>
                <div className={`font-bold ${color}`}>{rep} <span className="text-xs font-normal">{label}</span></div>
              </div>
              <div className="absolute right-0 top-full mt-1 z-50 hidden group-hover:block bg-gray-900 border border-gray-600 rounded-lg shadow-xl p-3 min-w-[220px] text-xs">
                <div className="font-semibold text-gray-200 mb-2">Reputation — {rep}/110</div>
                <div className={`mb-2 ${color}`}>{revenueImpact}</div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-2">
                  <div className={`h-full rounded-full ${rep >= 90 ? 'bg-emerald-500' : rep >= 70 ? 'bg-yellow-400' : rep >= 50 ? 'bg-orange-400' : 'bg-red-500'}`} style={{ width: `${Math.min(100, rep)}%` }} />
                </div>
                <div className="space-y-0.5 text-gray-400">
                  <div>Starts at 70. Recovers +0.5/day naturally.</div>
                  <div>Negative events (outages, DDoS) lower it.</div>
                  <div>Positive events (contracts) can push above 100.</div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* PUE */}
        {pue != null && (
          <div className="flex items-center gap-1 px-2 py-1 first:pl-0 last:pr-0 whitespace-nowrap shrink-0"
            title={pue >= 20 ? "PUE is very high — add more servers to spread infrastructure overhead across more IT load." : "Power Usage Effectiveness — lower is better. PUE 1.0 = perfect, all power goes to IT."}>
            <Gauge className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-xs text-gray-400">PUE</div>
              {pue >= 20
                ? <div className="font-bold text-gray-500">N/A <span className="text-xs font-normal text-gray-600">add servers</span></div>
                : <div className={`font-bold ${pueLabel?.cls ?? 'text-gray-300'}`}>{pue.toFixed(2)} <span className="text-xs font-normal">{pueLabel?.text}</span></div>
              }
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default StatsBar;
